import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { PromptService } from '../promtp-service/prompt.service';
import { PointsApiConfiguration } from 'src/app/api/proxy/points/points-api-configuration';
import { consumerHistoryPost, consumerCurrentBalanceGet, consumerGameTokenGet, consumerLeaderboardGet } from 'src/app/api/proxy/points/functions';
import { ActiveLeagueModel, CurrentBalanceModel, LeaderboardModel, PointsHistoryRequest, PointsHistoryResponse } from 'src/app/api/proxy/points/points-models';
import { SecureStorageService } from '../storage/secure-storage.service';
import { SecureStorageKey } from '../storage/models/secure-storage-key.enum';
import { EventService } from '../events/event.service';
import { BaseApiService } from '../base-api-service/base-api.service';
import { TokenService } from '../token/token.service';
import { LocaleService } from '../locale-service/locale.service';

@Injectable({
  providedIn: 'root',
})
export class PointsService extends BaseApiService {
  private NETWORK_TIMEOUT = 25000;

  constructor(
    protected _PromptService: PromptService,
    private http: HttpClient,
    private pointsCfg: PointsApiConfiguration,
    private _tokenService: TokenService,
    private _localeService: LocaleService,
  ) {
    super(_PromptService);
  }

  public async loadMonth(yearNumber: number, month: number, forceRefresh: boolean = false): Promise<PointsHistoryResponse | null> {
    // Get cached data
    let model = await this.getPointsHistoryStatic();
    
    // Check if we need to force refresh based on cache age
    const now = new Date().getTime();
    const oneDay = 60 * 60 * 1000 * 24; // 24 hours in milliseconds
    const lastFetchTime = await SecureStorageService.get(SecureStorageKey.LastPointsHistoryRefresh);

    if (!lastFetchTime || now - parseInt(lastFetchTime) > oneDay) {
      forceRefresh = true;
    }

    // Return cached data if not forcing refresh
    if (model && !forceRefresh) {
      return model;
    }

    // Always fetch from 1st of previous month up to and including today
    const today = new Date();
    const previousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const fromDate = new Date(previousMonth.getFullYear(), previousMonth.getMonth(), 1, 0, 0, 0);
    const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
    
    const request: PointsHistoryRequest = {
      startDate: fromDate.toISOString(),
      endDate: endDate.toISOString(),
    };

    const obs = consumerHistoryPost(this.http, this.pointsCfg.rootUrl, { body: request });
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'loadHistory');
    const result = httpResponse ? httpResponse.body : null;

    if (result) {
      // Save to cache with timestamp
      await SecureStorageService.set(SecureStorageKey.PointsHistory, JSON.stringify(result));
      await SecureStorageService.set(SecureStorageKey.LastPointsHistoryRefresh, now.toString());
      return result;
    }

    // Return cached data if API call failed or returned null
    return model;
  }

  private async getPointsHistoryStatic(): Promise<PointsHistoryResponse | null> {
    const cachedHistory = await SecureStorageService.get(SecureStorageKey.PointsHistory);
    if (cachedHistory) {
      try {
        return JSON.parse(cachedHistory) as PointsHistoryResponse;
      } catch {
        return null;
      }
    }
    return null;
  }

  public async currentBalanceStatic(): Promise<CurrentBalanceModel | null> {
    const mySummary = await SecureStorageService.get(SecureStorageKey.PointsBalance);
    let model: CurrentBalanceModel | null = null;
    if (mySummary) {
      try {
        model = JSON.parse(mySummary);
      } catch {
        model = null;
      }
    }

    return model;
  }


  public async currentBalance(forced: boolean): Promise<CurrentBalanceModel | null> {
    const accessToken = await this._tokenService.getTokenAsync();
    if (!accessToken) {
      return await this.currentBalanceStatic();
    }

    let cacheTimestamp: Date | null = null;
    let model = await this.currentBalanceStatic();
    if (model) {
      const ts = (model as any).fetchedAt;
      if (ts && !forced) {
        cacheTimestamp = new Date(ts);
        const now = new Date();
        const ageHours = (now.getTime() - cacheTimestamp.getTime()) / (1000 * 60 * 60);
        if(ageHours < 1) {
          return model; // Return cached model if it's fresh enough
        }
      }
    }

    const obs = consumerCurrentBalanceGet(this.http, this.pointsCfg.rootUrl);
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'loadHistory');
    const result = httpResponse ? httpResponse.body : null;

    if (result) {
      (result as any).fetchedAt = new Date().toISOString();
      await SecureStorageService.set(SecureStorageKey.PointsBalance, JSON.stringify(result));
      return result;
    }

    return model;
  }

  /**
   * Gets the active league for the authenticated user.
   * @param languageCode Optional language code for translations (default: current language or "en")
   * @param forceRefresh Whether to force refresh from server (default: false)
   * @returns Active league model with game token, or null if no active league
   */
  public async getActiveLeague(
    languageCode?: string, 
    forceRefresh: boolean = false
  ): Promise<ActiveLeagueModel | null> {
    // Get cached data
    let model = await this.getActiveLeagueStatic();
    
    // Check if we need to force refresh based on cache age
    const now = new Date().getTime();
    const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
    const lastFetchTime = await SecureStorageService.get(SecureStorageKey.LastActiveLeagueRefresh);

    if (!lastFetchTime || now - parseInt(lastFetchTime) > oneHour) {
      forceRefresh = true;
    }

    // Return cached data if not forcing refresh
    if (model && !forceRefresh) {
      // Emit event based on cached data
      const gameAvailable = model.canPlay === true && model.gameType != null;
      EventService.gameAvailable.emit(gameAvailable);
      return model;
    }

    // Call API with BCP-47 culture (falls back to LocaleService)
    const params: { languageCode?: string } = {};
    params.languageCode = (languageCode && languageCode.trim()) || this._localeService.getCulture();

    const obs = consumerGameTokenGet(this.http, this.pointsCfg.rootUrl, params);
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'loadLeague');
    const result = httpResponse ? httpResponse.body : null;

    if (result) {
      // Save to cache with timestamp
      await SecureStorageService.set(SecureStorageKey.ActiveLeague, JSON.stringify(result));
      await SecureStorageService.set(SecureStorageKey.LastActiveLeagueRefresh, now.toString());

      // Emit event for game availability (true if league exists, canPlay is true, and gameType exists)
      const gameAvailable = result.canPlay === true && result.gameType != null;
      EventService.gameAvailable.emit(gameAvailable);

      return result;
    }

    // Return cached data if API call failed or returned null
    // Emit event based on cached data
    if (model) {
      const gameAvailable = model.canPlay === true && model.gameType != null;
      EventService.gameAvailable.emit(gameAvailable);
    } else {
      EventService.gameAvailable.emit(false);
    }
    return model;
  }

  /**
   * Gets cached active league data from SecureStorage.
   * @returns Cached active league model or null
   */
  private async getActiveLeagueStatic(): Promise<ActiveLeagueModel | null> {
    const cachedLeague = await SecureStorageService.get(SecureStorageKey.ActiveLeague);
    if (cachedLeague) {
      try {
        return JSON.parse(cachedLeague) as ActiveLeagueModel;
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Gets cached active league data only (does not call API).
   * Use this to display league info without creating a pending game score.
   * @returns Cached active league model or null
   */
  public async getActiveLeagueCachedOnly(): Promise<ActiveLeagueModel | null> {
    return await this.getActiveLeagueStatic();
  }

  /**
   * Gets the leaderboard for the current active league.
   * @param topCount Number of top scores to return (default: 5, max: 100)
   * @param forceRefresh Whether to force refresh from server (default: false)
   * @returns Leaderboard model with top scores and member rank, or null if no active league
   */
  public async getLeaderboard(
    topCount: number = 5, 
    forceRefresh: boolean = false
  ): Promise<LeaderboardModel | null> {
    // Validate topCount (1-100)
    if (topCount < 1) topCount = 5;
    if (topCount > 100) topCount = 100;

    // Get cached data
    let model = await this.getLeaderboardStatic();
    
    // Check if we need to force refresh based on cache age
    const now = new Date().getTime();
    const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
    const lastFetchTime = await SecureStorageService.get(SecureStorageKey.LastLeaderboardRefresh);

    if (!lastFetchTime || now - parseInt(lastFetchTime) > fiveMinutes) {
      forceRefresh = true;
    }

    // Return cached data if not forcing refresh
    if (model && !forceRefresh) {
      return model;
    }

    // Call API with topCount parameter
    const params = {
      topCount: topCount
    };

    const obs = consumerLeaderboardGet(this.http, this.pointsCfg.rootUrl, params);
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'loadLeaderboard');
    const result = httpResponse ? httpResponse.body : null;

    if (result) {
      // Save to cache with timestamp
      await SecureStorageService.set(SecureStorageKey.Leaderboard, JSON.stringify(result));
      await SecureStorageService.set(SecureStorageKey.LastLeaderboardRefresh, now.toString());
      return result;
    }

    // Return cached data if API call failed or returned null
    return model;
  }

  /**
   * Gets cached leaderboard data from SecureStorage.
   * @returns Cached leaderboard model or null
   */
  private async getLeaderboardStatic(): Promise<LeaderboardModel | null> {
    const cachedLeaderboard = await SecureStorageService.get(SecureStorageKey.Leaderboard);
    if (cachedLeaderboard) {
      try {
        return JSON.parse(cachedLeaderboard) as LeaderboardModel;
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Clears all cached points data (history, balance, league, and leaderboard)
   * Use this when doing a pull-to-refresh to ensure fresh data
   */
  public async clearAllCache(): Promise<void> {
    await SecureStorageService.remove(SecureStorageKey.PointsHistory);
    await SecureStorageService.remove(SecureStorageKey.LastPointsHistoryRefresh);
    await SecureStorageService.remove(SecureStorageKey.PointsBalance);
    await SecureStorageService.remove(SecureStorageKey.ActiveLeague);
    await SecureStorageService.remove(SecureStorageKey.LastActiveLeagueRefresh);
    await SecureStorageService.remove(SecureStorageKey.Leaderboard);
    await SecureStorageService.remove(SecureStorageKey.LastLeaderboardRefresh);
  }
}
